import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, type Finding } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, RiskBar, Severity, Skel, Stat } from '../components/ui'
import { money, thaiDate, thaiDateTime } from '../lib/format'

/**
 * Remediation proposals (part 78).
 *
 * The planner produces a risk-ordered checklist from a finding. This page deliberately separates
 * the proposal from the sign-off: evidence is attached per step, a human reviewer verifies each
 * step, and only a human with owner/admin/audit_manager/reviewer authority can approve closure.
 * The UI never offers an "AI close" action; the guardrail is also enforced by the API and by a
 * database constraint, so hiding the button is convenience rather than security.
 */

type SeverityLevel = 'critical' | 'high' | 'medium' | 'low'
type CaseStatus = 'open' | 'in_progress' | 'pending_close' | 'closed' | 'cancelled'
type StepStatus = 'pending' | 'evidence_submitted' | 'verified'
type RiskLevel = 'critical' | 'high' | 'medium' | 'low'

interface RemediationCase {
  id: string
  code: string
  finding_id: string
  finding_title: string
  rule_code: string
  rule_title: string
  rule_version: number
  severity: SeverityLevel
  risk_score: number
  amount_at_risk_satang?: number
  status: CaseStatus
  owner_role: string
  due_on: string
  plan_source: string
  created_by?: string
  created_at: string
  updated_at: string
  close_requested_by?: string
  close_requested_at?: string
  close_note?: string
  close_approved_by?: string
  close_approved_at?: string
  close_approval_note?: string
  steps_total: number
  steps_with_evidence: number
  steps_verified: number
  evidence_count: number
  progress_bps: number
}

interface RemediationStep {
  id: string
  case_id: string
  key: string
  sequence: number
  title: string
  detail: string
  owner_role: string
  risk_score: number
  risk_level: RiskLevel
  required_evidence: boolean
  status: StepStatus
  due_on: string
  evidence_count: number
  verified_by?: string
  verified_by_name?: string
  verified_at?: string
  verify_note?: string
}

interface RemediationEvidence {
  id: string
  case_id: string
  step_id: string
  step_sequence: number
  evidence_kind: string
  reference_no: string
  uri?: string
  sha256?: string
  note?: string
  submitted_by?: string
  submitted_by_name?: string
  submitted_by_kind: string
  submitted_at: string
}

interface Blocker {
  code: string
  step_id?: string
  sequence?: number
  title?: string
  message: string
}

interface CaseDetail extends RemediationCase {
  steps: RemediationStep[]
  evidence: RemediationEvidence[]
  blockers: Blocker[]
  can_request_close: boolean
  can_approve_close: boolean
}

const CASE_QUERY = ['remediation-cases'] as const
const caseQuery = (id: string) => ['remediation-case', id] as const

const statusLabel: Record<CaseStatus, string> = {
  open: 'เปิดแผน',
  in_progress: 'กำลังแก้ไข',
  pending_close: 'รออนุมัติปิด',
  closed: 'ปิดแล้ว',
  cancelled: 'ยกเลิก',
}

const statusTone: Record<CaseStatus, string> = {
  open: 'dim',
  in_progress: 'sev-medium',
  pending_close: 'sev-high',
  closed: 'sev-low',
  cancelled: 'dim',
}

const stepStatusLabel: Record<StepStatus, string> = {
  pending: 'รอดำเนินการ',
  evidence_submitted: 'มีหลักฐานแล้ว',
  verified: 'ยืนยันแล้ว',
}

const stepStatusTone: Record<StepStatus, string> = {
  pending: 'dim',
  evidence_submitted: 'sev-medium',
  verified: 'sev-low',
}

const riskLabel: Record<RiskLevel, string> = {
  critical: 'วิกฤต',
  high: 'สูง',
  medium: 'ปานกลาง',
  low: 'ต่ำ',
}

const riskTone: Record<RiskLevel, string> = {
  critical: 'sev-critical',
  high: 'sev-high',
  medium: 'sev-medium',
  low: 'sev-low',
}

const roleLabel: Record<string, string> = {
  owner: 'เจ้าขององค์กร',
  admin: 'ผู้ดูแลระบบ',
  audit_manager: 'ผู้จัดการตรวจสอบ',
  auditor: 'ผู้ตรวจสอบ',
  reviewer: 'ผู้ทบทวน',
}

const evidenceKindLabel: Record<string, string> = {
  document: 'เอกสาร',
  screenshot: 'ภาพหน้าจอ',
  log: 'บันทึกระบบ',
  policy: 'นโยบาย',
  approval: 'เอกสารอนุมัติ',
  report: 'รายงาน',
  other: 'อื่น ๆ',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function percent(bps: number): string {
  return `${(bps / 100).toFixed(0)}%`
}

export function Remediation() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canWork = ['owner', 'admin', 'audit_manager', 'auditor'].includes(me?.role ?? '')
  const canVerify = ['owner', 'admin', 'audit_manager', 'reviewer'].includes(me?.role ?? '')
  const canApprove = canVerify
  const canRequestClose = ['owner', 'admin', 'audit_manager', 'auditor', 'reviewer'].includes(me?.role ?? '')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [findingId, setFindingId] = useState('')
  const [evidenceStep, setEvidenceStep] = useState<RemediationStep | null>(null)
  const [evidenceKind, setEvidenceKind] = useState('document')
  const [referenceNo, setReferenceNo] = useState('')
  const [evidenceURI, setEvidenceURI] = useState('')
  const [evidenceNote, setEvidenceNote] = useState('')
  const [verifyNote, setVerifyNote] = useState('')
  const [closeNote, setCloseNote] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const cases = useQuery({
    queryKey: CASE_QUERY,
    queryFn: () => api<RemediationCase[]>('/v1/remediation/cases'),
    staleTime: 10_000,
  })

  const findings = useQuery({
    queryKey: ['findings', 'remediation-picker'],
    queryFn: () => api<Finding[]>('/v1/findings?limit=100'),
    staleTime: 30_000,
  })

  useEffect(() => {
    if (!selectedId && cases.data && cases.data.length > 0) setSelectedId(cases.data[0].id)
  }, [cases.data, selectedId])

  const detail = useQuery({
    queryKey: caseQuery(selectedId ?? 'none'),
    queryFn: async () => {
      const body = await api<{ case: CaseDetail }>(`/v1/remediation/cases/${selectedId}`)
      return body.case
    },
    enabled: selectedId !== null,
  })

  const usedFindings = useMemo(
    () => new Set((cases.data ?? []).map((row) => row.finding_id)),
    [cases.data],
  )
  const candidates = useMemo(
    () => (findings.data ?? []).filter((f) =>
      !usedFindings.has(f.id) && !['closed', 'rejected', 'false_positive'].includes(f.status)),
    [findings.data, usedFindings],
  )

  const createPlan = useMutation({
    mutationFn: (id: string) => apiWrite<{ case: RemediationCase }>('/v1/remediation/cases', 'POST', { finding_id: id }),
    onSuccess: (body) => {
      setNotice('สร้างข้อเสนอแผนแก้ไขแล้ว ต้องแนบหลักฐานและให้ผู้ทบทวนยืนยันก่อนปิดเคส')
      setSelectedId(body.case.id)
      setFindingId('')
      void qc.invalidateQueries({ queryKey: CASE_QUERY })
    },
  })

  const refresh = (caseID: string) => {
    void qc.invalidateQueries({ queryKey: CASE_QUERY })
    void qc.invalidateQueries({ queryKey: caseQuery(caseID) })
  }

  const submitEvidence = useMutation({
    mutationFn: (input: { caseID: string; stepID: string }) =>
      apiWrite<{ evidence: RemediationEvidence; step: RemediationStep }>(
        `/v1/remediation/cases/${input.caseID}/steps/${input.stepID}/evidence`,
        'POST',
        {
          evidence_kind: evidenceKind,
          reference_no: referenceNo,
          uri: evidenceURI,
          note: evidenceNote,
          actor_kind: 'human',
        },
      ),
    onSuccess: (_body, input) => {
      setNotice('บันทึกหลักฐานแล้ว รอผู้ทบทวนยืนยันขั้นตอน')
      setEvidenceStep(null)
      setReferenceNo('')
      setEvidenceURI('')
      setEvidenceNote('')
      refresh(input.caseID)
    },
  })

  const verifyStep = useMutation({
    mutationFn: (input: { caseID: string; stepID: string }) =>
      apiWrite<{ step: RemediationStep }>(
        `/v1/remediation/cases/${input.caseID}/steps/${input.stepID}/verify`,
        'POST',
        { note: verifyNote, actor_kind: 'human' },
      ),
    onSuccess: (_body, input) => {
      setNotice('ยืนยันขั้นตอนแล้ว')
      setVerifyNote('')
      refresh(input.caseID)
    },
  })

  const requestClose = useMutation({
    mutationFn: (caseID: string) =>
      apiWrite<{ case: CaseDetail }>(`/v1/remediation/cases/${caseID}/request-close`, 'POST', {
        note: closeNote,
        actor_kind: 'human',
      }),
    onSuccess: (body) => {
      setNotice('ส่งคำขอปิดเคสแล้ว รอผู้อนุมัติที่เป็นมนุษย์ตรวจและอนุมัติ')
      setCloseNote('')
      refresh(body.case.id)
    },
  })

  const approveClose = useMutation({
    mutationFn: (caseID: string) =>
      apiWrite<{ case: CaseDetail }>(`/v1/remediation/cases/${caseID}/approve-close`, 'POST', {
        note: closeNote,
        actor_kind: 'human',
      }),
    onSuccess: (body) => {
      setNotice('ปิดเคสแล้ว บันทึกผู้อนุมัติและเวลาไว้ในประวัติ')
      setCloseNote('')
      refresh(body.case.id)
    },
  })

  const rows = cases.data ?? []
  const counts = useMemo(() => ({
    open: rows.filter((r) => r.status === 'open' || r.status === 'in_progress').length,
    pending: rows.filter((r) => r.status === 'pending_close').length,
    closed: rows.filter((r) => r.status === 'closed').length,
    critical: rows.filter((r) => r.severity === 'critical' && r.status !== 'closed').length,
  }), [rows])

  const current = detail.data
  const mutationError =
    createPlan.error ?? submitEvidence.error ?? verifyStep.error ?? requestClose.error ?? approveClose.error

  return (
    <div className="page">
      <PageHead
        title="การแก้ไขข้อตรวจพบ"
        sub="ข้อเสนอแผนแก้ไขจากข้อตรวจพบ เรียงตามความเสี่ยง พร้อมหลักฐานและการอนุมัติปิดโดยมนุษย์"
        actions={<span className="chip gold">ข้อเสนอโดย AI · อนุมัติโดยมนุษย์</span>}
      />

      {mutationError ? <div className="form-error" style={{ marginBottom: 16 }}>{errorText(mutationError, 'ดำเนินการไม่สำเร็จ')}</div> : null}
      {notice ? (
        <div className="card card-pad rise" style={{ marginBottom: 16, borderColor: 'rgba(200,169,106,0.35)' }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12.5 }}>{notice}</span>
            <button className="btn btn-sm btn-ghost" onClick={() => setNotice(null)}>ปิดข้อความ</button>
          </div>
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="แผนที่เปิดอยู่" value={counts.open} foot="รวมสถานะเปิดแผนและกำลังแก้ไข" />
        <Stat label="รออนุมัติปิด" value={counts.pending} foot="ต้องมีผู้อนุมัติที่เป็นมนุษย์" />
        <Stat label="ปิดแล้ว" value={counts.closed} foot="มีหลักฐานและผ่านการทบทวนครบ" />
        <Stat label="ความเสี่ยงวิกฤต" value={counts.critical} foot="ยังไม่ปิด และ severity ระดับวิกฤต" />
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="card-label">สร้างข้อเสนอแผนจากข้อตรวจพบ</div>
        <p className="dim" style={{ fontSize: 12, lineHeight: 1.7, marginTop: 7 }}>
          ระบบจะสังเคราะห์ขั้นตอนจากกฎและระดับความรุนแรงของข้อตรวจพบ โดยเรียงขั้นที่เสี่ยงที่สุดก่อน
          ข้อเสนอนี้ยังไม่แก้ไขข้อมูลใด ๆ และไม่สามารถปิดเคสได้ด้วยตนเอง
        </p>
        <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
          <select aria-label="กรองตามสถานะ"
            className="select"
            style={{ minWidth: 360, maxWidth: '100%' }}
            value={findingId}
            onChange={(e) => setFindingId(e.target.value)}
            disabled={findings.isLoading || candidates.length === 0}
          >
            <option value="">เลือกข้อตรวจพบที่ยังไม่มีแผนแก้ไข</option>
            {candidates.map((f) => (
              <option key={f.id} value={f.id}>
                {f.rule_code} · {f.title} · {riskLabel[f.severity]}
              </option>
            ))}
          </select>
          <button
            className="btn btn-primary"
            disabled={!canWork || !findingId || createPlan.isPending}
            onClick={() => findingId && createPlan.mutate(findingId)}
          >
            {createPlan.isPending ? 'กำลังสังเคราะห์…' : 'สร้างข้อเสนอแผน'}
          </button>
          {!canWork ? <span className="dim" style={{ fontSize: 11.5 }}>บทบาทของคุณสร้างแผนไม่ได้</span> : null}
        </div>
        {!findings.isLoading && candidates.length === 0 ? (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>ไม่มีข้อตรวจพบที่ยังไม่มีแผน หรือยังไม่มีข้อตรวจพบในองค์กรนี้</div>
        ) : null}
      </div>

      <div className="grid-main">
        <div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="card-label" style={{ margin: 0 }}>แผนแก้ไขทั้งหมด</span>
            <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} เคส</span>
          </div>

          {cases.isLoading ? <Skel h={280} /> : rows.length === 0 ? (
            <Empty title="ยังไม่มีแผนแก้ไข" body="เลือกข้อตรวจพบด้านบนเพื่อให้ระบบสร้างข้อเสนอแผนที่มีลำดับขั้นตามความเสี่ยง" />
          ) : (
            <div className="table-wrap">
              <table className="table data">
                <thead>
                  <tr>
                    <th>รหัสเคส / ข้อตรวจพบ</th>
                    <th>ความรุนแรง</th>
                    <th>สถานะ</th>
                    <th>ความคืบหน้า</th>
                    <th>กำหนดปิด</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} onClick={() => setSelectedId(row.id)}
                      style={row.id === selectedId ? { background: 'rgba(200,169,106,0.055)' } : undefined}>
                      <td style={{ maxWidth: 380 }}>
                        <div className="mono gold" style={{ fontSize: 11 }}>{row.code}</div>
                        <div style={{ lineHeight: 1.5, marginTop: 3 }}>{row.finding_title}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{row.rule_code} · ผู้รับผิดชอบ {roleLabel[row.owner_role] ?? row.owner_role}</div>
                      </td>
                      <td><Severity value={row.severity} /></td>
                      <td>
                        <span className={`sev ${statusTone[row.status]}`} style={{ fontSize: 11 }}>
                          {row.status !== 'open' ? <i className="sev-dot" /> : null}
                          {statusLabel[row.status]}
                        </span>
                      </td>
                      <td style={{ minWidth: 130 }}>
                        <div className="bar" style={{ width: 90 }}><i style={{ width: `${row.progress_bps / 100}%` }} /></div>
                        <div className="dim tnum" style={{ fontSize: 10.5, marginTop: 4 }}>
                          ยืนยัน {row.steps_verified}/{row.steps_total} · หลักฐาน {row.evidence_count}
                        </div>
                      </td>
                      <td className="tnum" style={{ fontSize: 12 }}>{thaiDate(row.due_on)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="stack">
          {!selectedId ? (
            <div className="card card-pad dim" style={{ fontSize: 12 }}>เลือกแผนแก้ไขเพื่อดูขั้นตอน หลักฐาน และการอนุมัติ</div>
          ) : detail.isLoading ? (
            <Skel h={420} />
          ) : !current ? (
            <div className="card card-pad dim" style={{ fontSize: 12 }}>โหลดรายละเอียดเคสไม่สำเร็จ</div>
          ) : (
            <>
              <div className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="mono gold" style={{ fontSize: 11 }}>{current.code}</div>
                    <h2 className="heading-m" style={{ marginTop: 7 }}>{current.finding_title}</h2>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 5 }}>
                      {current.rule_code} · {current.rule_title}
                    </div>
                  </div>
                  <span className={`sev ${statusTone[current.status]}`}>
                    {current.status !== 'open' ? <i className="sev-dot" /> : null}
                    {statusLabel[current.status]}
                  </span>
                </div>

                <div className="row" style={{ marginTop: 14, gap: 12, flexWrap: 'wrap' }}>
                  <Severity value={current.severity} />
                  <span className="chip">ผู้รับผิดชอบหลัก {roleLabel[current.owner_role] ?? current.owner_role}</span>
                  <span className="chip">กำหนดปิด {thaiDate(current.due_on)}</span>
                  <span className="chip">ข้อเสนอโดย AI</span>
                </div>

                <div className="row" style={{ marginTop: 14, gap: 16 }}>
                  <RiskBar score={current.risk_score} />
                  <span className="dim" style={{ fontSize: 11 }}>ความคืบหน้ายืนยัน {percent(current.progress_bps)}</span>
                  {current.amount_at_risk_satang ? (
                    <span className="dim" style={{ fontSize: 11 }}>มูลค่าที่ได้รับผลกระทบ {money(current.amount_at_risk_satang)}</span>
                  ) : null}
                </div>

                <div className="demo-note" style={{ marginTop: 14 }}>
                  AI ช่วยสังเคราะห์ลำดับขั้นและรวบรวมหลักฐานได้ แต่ไม่มีสิทธิ์ปิดเคส
                  ต้องมีผู้ทบทวนที่เป็นมนุษย์ยืนยันทุกขั้นและผู้อนุมัติที่มีอำนาจลงนามจึงจะปิดได้
                </div>
              </div>

              {current.blockers.length > 0 ? (
                <div className="card card-pad" style={{ borderColor: 'rgba(166,58,74,0.35)' }}>
                  <div className="card-label" style={{ color: 'var(--color-critical)' }}>สิ่งที่ยังขาดก่อนปิดเคส</div>
                  <div style={{ marginTop: 8 }}>
                    {current.blockers.map((b, i) => (
                      <div key={`${b.code}-${b.step_id ?? i}`} className="dim" style={{ fontSize: 11.5, lineHeight: 1.7 }}>
                        {b.sequence ? `ขั้นตอนที่ ${b.sequence}: ` : ''}{b.message}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
                  <span className="card-label" style={{ margin: 0 }}>ขั้นตอนเรียงตามความเสี่ยง</span>
                  <span className="chip">{current.steps.length} ขั้นตอน</span>
                </div>

                {current.steps.map((step) => {
                  const stepEvidence = current.evidence.filter((e) => e.step_id === step.id)
                  const editing = evidenceStep?.id === step.id
                  return (
                    <div key={step.id} style={{
                      padding: '14px 0', borderTop: '1px solid var(--color-hairline)',
                    }}>
                      <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
                        <span className="chip gold" style={{ minWidth: 28, justifyContent: 'center' }}>{step.sequence}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ lineHeight: 1.5 }}>{step.title}</div>
                          <div className="dim" style={{ fontSize: 11, lineHeight: 1.7, marginTop: 4 }}>{step.detail}</div>
                          <div className="row" style={{ marginTop: 7, gap: 8, flexWrap: 'wrap' }}>
                            <span className={`sev ${riskTone[step.risk_level]}`} style={{ fontSize: 10.5 }}>
                              <i className="sev-dot" />ความเสี่ยง {riskLabel[step.risk_level]} {step.risk_score}
                            </span>
                            <span className="dim" style={{ fontSize: 10.5 }}>ผู้รับผิดชอบ {roleLabel[step.owner_role] ?? step.owner_role}</span>
                            <span className="dim" style={{ fontSize: 10.5 }}>กำหนด {thaiDate(step.due_on)}</span>
                            <span className="dim" style={{ fontSize: 10.5 }}>หลักฐาน {step.evidence_count}</span>
                          </div>
                        </div>
                        <span className={`sev ${stepStatusTone[step.status]}`} style={{ fontSize: 10.5 }}>
                          {step.status !== 'pending' ? <i className="sev-dot" /> : null}
                          {stepStatusLabel[step.status]}
                        </span>
                      </div>

                      {stepEvidence.length > 0 ? (
                        <div style={{ marginTop: 10, paddingLeft: 40 }}>
                          {stepEvidence.map((e) => (
                            <div key={e.id} className="evidence-row">
                              <span className="dim">·</span>
                              <span>
                                <span className="mono" style={{ fontSize: 11 }}>{e.reference_no}</span>
                                <span className="dim" style={{ marginLeft: 8, fontSize: 10.5 }}>
                                  {evidenceKindLabel[e.evidence_kind] ?? e.evidence_kind}
                                  {e.note ? ` · ${e.note}` : ''}
                                  {e.submitted_by_name ? ` · โดย ${e.submitted_by_name}` : ''}
                                </span>
                              </span>
                              <span className="dim tnum" style={{ fontSize: 10 }}>{thaiDateTime(e.submitted_at)}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      <div className="row" style={{ marginTop: 9, paddingLeft: 40, gap: 8, flexWrap: 'wrap' }}>
                        {canWork && step.status !== 'verified' && current.status !== 'closed' ? (
                          <button className="btn btn-sm" onClick={() => {
                            setEvidenceStep(editing ? null : step)
                            setEvidenceKind('document')
                            setReferenceNo('')
                            setEvidenceURI('')
                            setEvidenceNote('')
                          }}>
                            {editing ? 'ยกเลิกการแนบ' : 'แนบหลักฐาน'}
                          </button>
                        ) : null}
                        {canVerify && step.status === 'evidence_submitted' && current.status !== 'closed' ? (
                          <button className="btn btn-sm" disabled={verifyStep.isPending}
                            onClick={() => verifyStep.mutate({ caseID: current.id, stepID: step.id })}>
                            {verifyStep.isPending ? 'กำลังยืนยัน…' : 'ยืนยันขั้นตอน'}
                          </button>
                        ) : null}
                        {step.status === 'verified' ? (
                          <span className="dim" style={{ fontSize: 10.5 }}>
                            ผู้ทบทวน {step.verified_by_name || '—'}{step.verified_at ? ` · ${thaiDateTime(step.verified_at)}` : ''}
                          </span>
                        ) : null}
                      </div>

                      {editing ? (
                        <div style={{ marginTop: 12, paddingLeft: 40 }}>
                          <div className="grid-2">
                            <div className="field" style={{ marginTop: 0 }}>
                              <label htmlFor={`kind-${step.id}`}>ประเภทหลักฐาน</label>
                              <select id={`kind-${step.id}`} className="select" style={{ width: '100%' }}
                                value={evidenceKind} onChange={(e) => setEvidenceKind(e.target.value)}>
                                {Object.entries(evidenceKindLabel).map(([value, label]) => (
                                  <option key={value} value={value}>{label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="field" style={{ marginTop: 0 }}>
                              <label htmlFor={`ref-${step.id}`}>เลขที่อ้างอิง</label>
                              <input id={`ref-${step.id}`} className="input" maxLength={120} value={referenceNo}
                                onChange={(e) => setReferenceNo(e.target.value)} placeholder="เช่น DOC-2026-0042" />
                            </div>
                          </div>
                          <div className="field">
                            <label htmlFor={`uri-${step.id}`}>ลิงก์เอกสาร (ถ้ามี)</label>
                            <input id={`uri-${step.id}`} className="input" maxLength={500} value={evidenceURI}
                              onChange={(e) => setEvidenceURI(e.target.value)} placeholder="https://…" />
                          </div>
                          <div className="field">
                            <label htmlFor={`note-${step.id}`}>บันทึกเพิ่มเติม</label>
                            <textarea id={`note-${step.id}`} className="input" rows={2} maxLength={2000} value={evidenceNote}
                              onChange={(e) => setEvidenceNote(e.target.value)}
                              style={{ width: '100%', height: 'auto', padding: '10px 12px' }}
                              placeholder="สิ่งที่หลักฐานนี้พิสูจน์ และผู้จัดเก็บ" />
                          </div>
                          <div className="row" style={{ marginTop: 10 }}>
                            <button className="btn btn-primary btn-sm" disabled={submitEvidence.isPending || !referenceNo.trim()}
                              onClick={() => submitEvidence.mutate({ caseID: current.id, stepID: step.id })}>
                              {submitEvidence.isPending ? 'กำลังบันทึก…' : 'บันทึกหลักฐาน'}
                            </button>
                            <span className="dim" style={{ fontSize: 10.5 }}>หลักฐานที่บันทึกแล้วแก้ไขไม่ได้</span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>

              <div className="card card-pad">
                <div className="card-label">การปิดเคส</div>
                <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.7, marginTop: 7 }}>
                  ขอปิดได้เมื่อมีหลักฐานครบทุกขั้น แต่อนุมัติปิดได้เมื่อผู้ทบทวนยืนยันครบทุกขั้นแล้วเท่านั้น
                  ผู้อนุมัติต้องเป็นมนุษย์ในบทบาท owner, admin, audit_manager หรือ reviewer
                </p>

                {current.status === 'closed' ? (
                  <div style={{ marginTop: 10 }}>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <span className="chip gold">ปิดเมื่อ {current.close_approved_at ? thaiDateTime(current.close_approved_at) : '—'}</span>
                      <span className="chip">ผู้อนุมัติ {current.close_approved_by ?? '—'}</span>
                    </div>
                    {current.close_approval_note ? <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>ความเห็นผู้อนุมัติ: {current.close_approval_note}</div> : null}
                  </div>
                ) : (
                  <>
                    <div className="field">
                      <label htmlFor="remediation-close-note">บันทึกประกอบคำขอ / การอนุมัติ</label>
                      <textarea id="remediation-close-note" className="input" rows={2} maxLength={2000} value={closeNote}
                        onChange={(e) => setCloseNote(e.target.value)}
                        style={{ width: '100%', height: 'auto', padding: '10px 12px' }}
                        placeholder="สรุปผลการแก้ไขหรือเหตุผลประกอบการปิดเคส" />
                    </div>
                    <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
                      {current.status !== 'pending_close' ? (
                        <button className="btn btn-primary" disabled={!canRequestClose || !current.can_request_close || requestClose.isPending}
                          onClick={() => requestClose.mutate(current.id)}>
                          {requestClose.isPending ? 'กำลังส่งคำขอ…' : 'ขอปิดเคส'}
                        </button>
                      ) : (
                        <button className="btn btn-primary" disabled={!canApprove || !current.can_approve_close || approveClose.isPending}
                          onClick={() => approveClose.mutate(current.id)}>
                          {approveClose.isPending ? 'กำลังอนุมัติ…' : 'อนุมัติปิดเคส'}
                        </button>
                      )}
                      {!current.can_request_close && current.status !== 'pending_close' ? (
                        <span className="dim" style={{ fontSize: 11 }}>ยังขาดหลักฐานหรือยังไม่ได้แนบครบทุกขั้น</span>
                      ) : null}
                      {current.status === 'pending_close' && !current.can_approve_close ? (
                        <span className="dim" style={{ fontSize: 11 }}>ยังมีขั้นตอนที่รอการยืนยันจากผู้ทบทวน</span>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
